import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

/**
 * FIFACarousel - A 3D card carousel inspired by FIFA player selection
 * Features: Center card prominent, side cards recessed, smooth rotation
 */
const FIFACarousel = ({ 
    cards, 
    renderCard, 
    onCardSelect,
    emptyMessage = "No cards to display"
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    
    if (!cards || cards.length === 0) {
        return (
            <div className="flex items-center justify-center py-16">
                <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                        <span className="text-3xl">📋</span>
                    </div>
                    <p className="text-gray-500">{emptyMessage}</p>
                </div>
            </div>
        );
    }
    
    const goLeft = () => {
        setCurrentIndex((prev) => (prev - 1 + cards.length) % cards.length);
    };
    
    const goRight = () => {
        setCurrentIndex((prev) => (prev + 1) % cards.length);
    };
    
    // Calculate positions for visible cards
    // Cards are arranged as if on a circular display, facing outward
    const getCardStyle = (index) => {
        const diff = index - currentIndex;
        const normalizedDiff = ((diff + cards.length) % cards.length);
        const actualDiff = normalizedDiff > cards.length / 2 ? normalizedDiff - cards.length : normalizedDiff;
        
        // Only show 3 cards: center, left, right
        if (Math.abs(actualDiff) > 1) {
            return {
                opacity: 0,
                transform: `translateX(${actualDiff > 0 ? 200 : -200}%) scale(0.5) rotateY(${actualDiff > 0 ? 35 : -35}deg)`,
                zIndex: 0,
                pointerEvents: 'none'
            };
        }
        
        if (actualDiff === 0) {
            // Center card - full size, facing forward
            return {
                opacity: 1,
                transform: 'translateX(0) scale(1) rotateY(0deg)',
                zIndex: 30,
                pointerEvents: 'auto'
            };
        } else if (actualDiff === -1 || (actualDiff === cards.length - 1)) {
            // Left card - facing outward, parallel (no tilt)
            return {
                opacity: 0.9,
                transform: 'translateX(-88%) scale(0.82) rotateY(-25deg)',
                zIndex: 20,
                pointerEvents: 'auto',
                cursor: 'pointer'
            };
        } else if (actualDiff === 1 || (actualDiff === -(cards.length - 1))) {
            // Right card - facing outward, parallel (no tilt)
            return {
                opacity: 0.9,
                transform: 'translateX(88%) scale(0.82) rotateY(25deg)',
                zIndex: 20,
                pointerEvents: 'auto',
                cursor: 'pointer'
            };
        }
        
        return {
            opacity: 0,
            transform: 'translateX(0) scale(0.5)',
            zIndex: 0,
            pointerEvents: 'none'
        };
    };
    
    const handleCardClick = (index) => {
        if (index !== currentIndex) {
            setCurrentIndex(index);
        } else if (onCardSelect) {
            onCardSelect(cards[index]);
        }
    };

    return (
        <div className="relative w-full py-8">
            {/* Carousel Container */}
            <div 
                className="relative mx-auto flex items-center justify-center"
                style={{ 
                    height: '540px',
                    perspective: '1200px',
                    perspectiveOrigin: 'center center'
                }}
            >
                {/* Navigation - Left */}
                <Button
                    variant="outline"
                    onClick={goLeft}
                    className="absolute left-4 z-40 h-12 w-12 rounded-full bg-white shadow-lg border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-all"
                    style={{ top: '50%', transform: 'translateY(-50%)' }}
                >
                    <ChevronLeft className="h-6 w-6" />
                </Button>
                
                {/* Cards Container */}
                <div 
                    className="relative w-full max-w-4xl mx-auto flex items-center justify-center"
                    style={{ transformStyle: 'preserve-3d' }}
                >
                    {cards.map((card, index) => {
                        const style = getCardStyle(index);
                        const isCenter = index === currentIndex;
                        
                        return (
                            <div
                                key={card.candidate_id || card.assessment_id || card.id || index}
                                className="absolute transition-all duration-500 ease-out"
                                style={{
                                    ...style,
                                    transformStyle: 'preserve-3d',
                                    left: '50%',
                                    marginLeft: '-160px', // Half of card width
                                }}
                                onClick={() => handleCardClick(index)}
                            >
                                <div className={`
                                    transition-shadow duration-300
                                    ${isCenter ? 'shadow-2xl shadow-purple-200' : 'shadow-lg'}
                                `}>
                                    {renderCard(card, isCenter)}
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {/* Navigation - Right */}
                <Button
                    variant="outline"
                    onClick={goRight}
                    className="absolute right-4 z-40 h-12 w-12 rounded-full bg-white shadow-lg border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-all"
                    style={{ top: '50%', transform: 'translateY(-50%)' }}
                >
                    <ChevronRight className="h-6 w-6" />
                </Button>
            </div>
            
            {/* Card Counter / Dots */}
            <div className="flex items-center justify-center mt-4 space-x-2">
                {cards.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`
                            w-2.5 h-2.5 rounded-full transition-all duration-300
                            ${index === currentIndex 
                                ? 'bg-purple-600 w-8' 
                                : 'bg-gray-300 hover:bg-gray-400'
                            }
                        `}
                    />
                ))}
            </div>
            
            {/* Card Count */}
            <p className="text-center text-sm text-gray-500 mt-2">
                {currentIndex + 1} of {cards.length} candidates
            </p>
        </div>
    );
};

export default FIFACarousel;
